import type { ScheduleBlockedRule } from '@weave/schema-gen-ts/dist/schemas/ai-settings/v1/ai_settings.pb';
import type { V1 as AiSettingsV1 } from '@frontend/api-ai-settings';

type ScheduleInput = AiSettingsV1.Types.UpdateScheduleIO['input']['schedule'];

// Enums for availability rule values
export enum AvailabilityScheduleType {
  RECURRENCE = 'RECURRENCE',
  WEEKLY = 'WEEKLY',
  SPECIFIC_DATE = 'SPECIFIC_DATE',
}

export enum AvailabilityDayOfWeek {
  SUNDAY = 'SUNDAY',
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
}

// Time slot interface
export interface TimeSlot {
  start: string; // Format: "HH:MM:SS"
  end: string; // Format: "HH:MM:SS"
}

// Service information with name resolved
export interface ResolvedService {
  id: string;
  name: string;
}

// Provider/Staff information with name resolved
export interface ResolvedProvider {
  id: string;
  name: string;
}

export enum BookingInterval {
  NONE = 'NONE',
  TOP_OF_HOUR = 'TOP_OF_HOUR',
  EVERY_HOUR = 'EVERY_HOUR',
  EVERY_TEN_MINUTES = 'EVERY_TEN_MINUTES',
  EVERY_FIFTEEN_MINUTES = 'EVERY_FIFTEEN_MINUTES',
  EVERY_TWENTY_MINUTES = 'EVERY_TWENTY_MINUTES',
  HALF_AN_HOUR = 'HALF_AN_HOUR',
}

export enum RecurrenceUnit {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
}

export type TimeUnit = 'days' | 'weeks' | 'months';

export type BookingCadence = BookingInterval | '';

// Type for individual availability rule/schedule with resolved names
export interface AvailabilityRule {
  id: string;
  scheduleType: AvailabilityScheduleType;
  timeSlot: TimeSlot;
  days: AvailabilityDayOfWeek[];
  /** ISO date string, or empty string for WEEKLY rules. */
  date: string;
  /** Only meaningful for RECURRENCE rules. */
  recurrenceUnit: RecurrenceUnit | string;
  /** Only meaningful for RECURRENCE rules. */
  recurrenceCount: number;
  bookingInterval: BookingInterval | string;
  /** Resolved from staffIds — use .id for form selection, .name for display. */
  providers: ResolvedProvider[];
  /** Resolved from serviceIds — use .id for form selection, .name for display. */
  services: ResolvedService[];
  // Server-owned fields — not editable in the form, passed through on update
  businessId?: string;
  locationId?: string;
  syncedFromPms?: boolean;
  scheduleBreaks?: ScheduleInput['scheduleBreaks'];
}

// Type for the actual API response operatory (with optional fields)
export interface ApiOperatory {
  id?: string;
  businessId?: string;
  name?: string;
  description?: string;
  capacity?: number;
  serviceIds?: string[];
  locationIds?: string[];
}

// Type for the combined operatory with its availability rules
export interface OperatoryWithAvailabilityRules extends ApiOperatory {
  availabilityRules: AvailabilityRule[];
}

// Combined response data structure
export interface CombinedOperatoriesData {
  operatories: OperatoryWithAvailabilityRules[];
  businessId?: string;
}

// Resolved resource (operatory) with id and name
export interface ResolvedResource {
  id: string;
  name: string;
}

// ScheduleBlockedRule with staffIds/serviceIds/resourceIds replaced by resolved {id, name} objects
export interface EnrichedScheduleBlockedRule
  extends Omit<ScheduleBlockedRule, 'staffIds' | 'serviceIds' | 'resourceIds'> {
  staffIds: ResolvedProvider[];
  serviceIds: ResolvedService[];
  resourceIds: ResolvedResource[];
}
